import { addAttachments } from '@blocksuite/affine-block-attachment';
import { EdgelessFrameManagerIdentifier } from '@blocksuite/affine-block-frame';
import { addImages } from '@blocksuite/affine-block-image';
import {
  CanvasRenderer,
  DefaultTool,
  EdgelessCRUDIdentifier,
  ExportManager,
  getSurfaceComponent,
} from '@blocksuite/affine-block-surface';
import { splitIntoLines } from '@blocksuite/affine-gfx-text';
import type {
  EmbedCardStyle,
  ShapeElementModel,
} from '@blocksuite/affine-model';
import {
  BookmarkStyles,
  DEFAULT_NOTE_HEIGHT,
  DEFAULT_NOTE_WIDTH,
  FrameBlockModel,
  MAX_IMAGE_WIDTH,
} from '@blocksuite/affine-model';
import {
  beginDatabaseCut,
  ClipboardAdapter,
  containsDatabaseBlock,
  decodeClipboardBlobs,
} from '@blocksuite/affine-shared/adapters';
import {
  CANVAS_EXPORT_IGNORE_TAGS,
  EMBED_CARD_HEIGHT,
  EMBED_CARD_WIDTH,
} from '@blocksuite/affine-shared/consts';
import {
  allowsFreeCanvasContent,
  EmbedOptionProvider,
  ParseDocUrlProvider,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import {
  convertToPng,
  isInsidePageEditor,
  isTopLevelBlock,
  isUrlInClipboard,
  matchModels,
  referenceToNode,
} from '@blocksuite/affine-shared/utils';
import { DisposableGroup } from '@blocksuite/global/disposable';
import {
  Bound,
  getCommonBound,
  type IBound,
  type IVec,
  Vec,
} from '@blocksuite/global/gfx';
import type {
  EditorHost,
  SurfaceSelection,
  UIEventStateContext,
} from '@blocksuite/std';
import {
  compareLayer,
  type GfxBlockElementModel,
  GfxControllerIdentifier,
  type GfxPrimitiveElementModel,
  loadGroupMemberParts,
  type SerializedElement,
} from '@blocksuite/std/gfx';
import {
  BlockModel,
  type BlockSnapshot,
  type SliceSnapshot,
  toDraftModel,
} from '@blocksuite/store';
import * as Y from 'yjs';

import { PageClipboard } from '../../clipboard/index.js';
import { getSortedCloneElements } from '../utils/clone-utils.js';
import { isCanvasElementWithText, isImageBlock } from '../utils/query.js';
import { createElementsFromClipboardDataCommand } from './command.js';
import {
  isPureFileInClipboard,
  prepareClipboardData,
  tryGetSvgFromClipboard,
} from './utils.js';

const BLOCKSUITE_SURFACE = 'blocksuite/surface';

const IMAGE_PADDING = 5; // for rotated shapes some padding is needed
const MAX_CANVAS_SIZE = 4096; // Maximum canvas dimension to prevent memory issues

interface CanvasExportOptions {
  dpr?: number;
  padding?: number;
  background?: string;
}

export class EdgelessClipboardController extends PageClipboard {
  static override key = 'affine-edgeless-clipboard';

  private readonly _initEdgelessClipboard = () => {
    this.std.event.add('copy', ctx => {
      const { surfaceSelections, selectedIds } = this.selectionManager;

      if (selectedIds.length === 0) return false;

      this._onCopy(ctx, surfaceSelections).catch(console.error);
      return;
    });

    this.std.event.add('paste', ctx => {
      if (!allowsFreeCanvasContent(this.std)) return;
      this._onPaste(ctx).catch(console.error);
    });

    this.std.event.add('cut', ctx => {
      if (!allowsFreeCanvasContent(this.std)) return;
      this._onCut(ctx).catch(console.error);
    });
  };

  private readonly _onCopy = async (
    _context: UIEventStateContext,
    surfaceSelection: SurfaceSelection[],
    cutToken?: string
  ) => {
    const event = _context.get('clipboardState').raw;
    event.preventDefault();

    const selected = this.selectionManager.selectedElements;

    // when note active, handle copy like page mode
    if (surfaceSelection[0] && surfaceSelection[0].editing) {
      // use build-in copy handler in rich-text when copy in surface text element
      if (isCanvasElementWithText(selected[0])) return;
      this.onPageCopy(_context);
      return;
    }

    // A copied group or frame carries all its members.
    const loading = loadGroupMemberParts(this.std.store, selected);
    if (loading) await loading;
    const elements = getSortedCloneElements(selected);

    // Only when an image is selected, it can be pasted normally to page mode.
    if (elements.length === 1 && isImageBlock(elements[0])) {
      const element = elements[0];
      const sourceId = element.props.sourceId$.peek();
      if (!sourceId) return;

      await this.std.clipboard.writeToClipboard(async items => {
        const job = this.std.store.getTransformer();
        await job.assetsManager.readFromBlob(sourceId);

        let blob = job.assetsManager.getAssets().get(sourceId) ?? null;
        if (!blob) {
          return items;
        }

        let type = blob.type;
        let supported = false;

        try {
          supported = ClipboardItem?.supports(type) ?? false;
        } catch (err) {
          console.error(err);
        }

        // TODO(@fundon): when converting jpeg to png, image may become larger and exceed the limit.
        if (!supported) {
          type = 'image/png';
          blob = await convertToPng(blob);
        }

        if (blob) {
          return {
            ...items,
            [`${type}`]: blob,
          };
        }

        return items;
      });

      return;
    }

    await this.std.clipboard.writeToClipboard(async _items => {
      const data = await prepareClipboardData(elements, this.std, cutToken);
      return {
        ..._items,
        [BLOCKSUITE_SURFACE]: JSON.stringify(data),
      };
    });
  };

  private readonly _onCut = async (_context: UIEventStateContext) => {
    const { surfaceSelections, selectedElements } = this.selectionManager;

    if (selectedElements.length === 0) return;

    const event = _context.get('clipboardState').event;
    event.preventDefault();

    if (surfaceSelections[0]?.editing) {
      // use build-in cut handler in rich-text when cut in surface text element
      if (isCanvasElementWithText(selectedElements[0])) return;
      this.onPageCut(_context);
      return;
    }

    const loading = loadGroupMemberParts(this.std.store, selectedElements);
    if (loading) await loading;
    const elements = getSortedCloneElements(
      this.selectionManager.selectedElements
    );
    const cut = beginDatabaseCut(
      this.std.store,
      elements
        .filter(
          (element): element is GfxBlockElementModel =>
            element instanceof BlockModel
        )
        .map(toDraftModel)
    );
    try {
      await this._onCopy(_context, surfaceSelections, cut.token);
      // A database stays in place until its paste moves it; blocks holding
      // one leave together with it.
      const kept = elements.filter(
        (element): element is GfxBlockElementModel =>
          element instanceof BlockModel && containsDatabaseBlock(element)
      );
      cut.removeOnPaste(kept);
      const keep = new Set<unknown>(kept);
      this.doc.transact(() => {
        this.crud
          .deleteElements(
            elements.filter(element => !keep.has(element)),
            { moving: true }
          )
          .catch(console.error);
      });
      cut.complete();
    } catch (error) {
      cut.abort();
      throw error;
    }

    this.selectionManager.set({
      editing: false,
      elements: [],
    });
  };

  private readonly _onPaste = async (_context: UIEventStateContext) => {
    if (
      document.activeElement instanceof HTMLInputElement ||
      document.activeElement instanceof HTMLTextAreaElement
    ) {
      return;
    }
    const event = _context.get('clipboardState').raw;
    event.preventDefault();

    const { surfaceSelections, selectedElements } = this.selectionManager;

    if (surfaceSelections[0]?.editing) {
      // use build-in paste handler in rich-text when paste in surface text element
      if (isCanvasElementWithText(selectedElements[0])) return;
      this.onPagePaste(_context);
      return;
    }

    const data = event.clipboardData;
    if (!data) return;

    if (!this.surface) return;

    const lastMousePos = this.toolManager.lastMousePos$.peek();
    const point: IVec = [lastMousePos.x, lastMousePos.y];

    if (isPureFileInClipboard(data)) {
      const files = data.files;
      if (files.length === 0) return;

      const imageFiles: File[] = [],
        attachmentFiles: File[] = [];

      [...files].forEach(file => {
        if (file.type.startsWith('image/')) {
          imageFiles.push(file);
        } else {
          attachmentFiles.push(file);
        }
      });

      // when only images in clipboard, add image-blocks else add all files as attachments
      if (attachmentFiles.length === 0) {
        await addImages(this.std, imageFiles, {
          point,
          maxWidth: MAX_IMAGE_WIDTH,
          shouldTransformPoint: false,
        });
      } else {
        addAttachments(this.std, [...files], point, false);
      }

      this.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
        control: 'canvas:paste',
        page: 'whiteboard editor',
        module: 'toolbar',
        segment: 'toolbar',
        type: attachmentFiles.length === 0 ? 'image' : 'attachment',
      });

      return;
    }

    if (isUrlInClipboard(data)) {
      const url = data.getData('text/plain');
      const { x, y } = this.toolManager.lastMousePos$.peek();

      // try to interpret url as affine doc url
      const parseDocUrlService = this.std.getOptional(ParseDocUrlProvider);
      const docUrlInfo = parseDocUrlService?.parseDocUrl(url);
      const options: Record<string, unknown> = {};

      let flavour = 'affine:bookmark';
      let style: EmbedCardStyle = BookmarkStyles[0];
      let isInternalLink = false;
      let isLinkedBlock = false;

      if (docUrlInfo) {
        const { docId: pageId, ...params } = docUrlInfo;

        flavour = 'affine:embed-linked-doc';
        style = 'vertical';

        isInternalLink = true;
        isLinkedBlock = referenceToNode({ pageId, params });
        options.pageId = pageId;
        if (params) options.params = params;
      } else {
        options.url = url;

        const embedOptions = this.std
          .get(EmbedOptionProvider)
          .getEmbedBlockOptions(url);
        if (embedOptions) {
          flavour = embedOptions.flavour;
          style = embedOptions.styles[0];
        }
      }

      const width = EMBED_CARD_WIDTH[style];
      const height = EMBED_CARD_HEIGHT[style];

      options.xywh = Bound.fromCenter(
        Vec.toVec({
          x,
          y,
        }),
        width,
        height
      ).serialize();
      options.style = style;

      const id = this.crud.addBlock(flavour, options, this.surface.model.id);

      this.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
        control: 'canvas:paste',
        page: 'whiteboard editor',
        module: 'toolbar',
        segment: 'toolbar',
        type: flavour.split(':')[1],
      });

      this.std
        .getOptional(TelemetryProvider)
        ?.track(isInternalLink ? 'LinkedDocCreated' : 'Link', {
          page: 'whiteboard editor',
          segment: 'whiteboard',
          category: 'pasted link',
          other: isInternalLink ? 'existing doc' : 'external link',
          type: isInternalLink ? (isLinkedBlock ? 'block' : 'doc') : 'link',
        });

      this.selectionManager.set({
        editing: false,
        elements: [id],
      });

      return;
    }

    const svg = tryGetSvgFromClipboard(data);
    if (svg) {
      await addImages(this.std, [svg], { point, maxWidth: MAX_IMAGE_WIDTH });
      return;
    }
    try {
      // check for surface elements in clipboard
      const json = this.std.clipboard.readFromClipboard(data);
      const mayBeSurfaceDataJson = json[BLOCKSUITE_SURFACE];
      if (mayBeSurfaceDataJson !== undefined) {
        const elementsRawData = JSON.parse(mayBeSurfaceDataJson);
        const { snapshot, blobs } = elementsRawData;
        const job = this.std.store.getTransformer();
        const map = job.assetsManager.getAssets();
        decodeClipboardBlobs(blobs, map);
        // Match the existing clipboard upload budget. Independent blob
        // writes need not add their individual storage latencies together.
        const blobIds = [...map.keys()];
        for (let offset = 0; offset < blobIds.length; offset += 5) {
          await Promise.all(
            blobIds
              .slice(offset, offset + 5)
              .map(blobId => job.assetsManager.writeToBlob(blobId))
          );
        }
        await this._pasteShapesAndBlocks(snapshot, point);
        return;
      }
      // check for slice snapshot in clipboard
      const mayBeSliceDataJson = json[ClipboardAdapter.MIME];
      if (mayBeSliceDataJson === undefined) return;
      const clipData = JSON.parse(mayBeSliceDataJson);
      const sliceSnapShot = clipData?.snapshot as SliceSnapshot;
      await this._pasteTextContentAsNote(sliceSnapShot.content, point);
    } catch {
      // if it is not parsable
      await this._pasteTextContentAsNote(data.getData('text/plain'), point);
    }
  };

  private get _exportManager() {
    return this.std.getOptional(ExportManager);
  }

  private get doc() {
    return this.std.store;
  }

  private get selectionManager() {
    return this.gfx.selection;
  }

  private get surface() {
    return getSurfaceComponent(this.std);
  }

  private get frame() {
    return this.std.get(EdgelessFrameManagerIdentifier);
  }

  private get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  private get crud() {
    return this.std.get(EdgelessCRUDIdentifier);
  }

  private get toolManager() {
    return this.gfx.tool;
  }

  private _checkCanContinueToCanvas(
    host: EditorHost,
    pathName: string,
    editorMode: boolean
  ) {
    if (
      location.pathname !== pathName ||
      isInsidePageEditor(host) !== editorMode
    ) {
      throw new Error('Unable to export content to canvas');
    }
  }

  /**
   * Render blocks to canvas using hook or default per-block rendering.
   * Hook allows StreamLain to provide optimized rendering (parallel, etc.)
   */
  private async _renderBlocksWithHook(
    nodeElements: GfxBlockElementModel[],
    bound: IBound,
    padding: number,
    ctx: CanvasRenderingContext2D,
    html2canvas: (
      element: HTMLElement,
      options?: Record<string, unknown>
    ) => Promise<HTMLCanvasElement>,
    html2canvasOption: Record<string, unknown>
  ) {
    // Hook for custom rendering (StreamLain: parallel rendering)
    const renderHook = (window as any).__streamlainScreenshotHooks
      ?.renderBlocks;
    if (renderHook) {
      const handled = await renderHook({
        nodeElements,
        bound,
        padding,
        ctx,
        html2canvas,
        html2canvasOption,
        getBlockComponent: (id: string) =>
          this.std.view.getBlock(id) as HTMLElement | null,
        getFrameElements: (
          frame: GfxBlockElementModel,
          includeInner: boolean
        ) => this.frame.getElementsInFrameBound(frame as any, includeInner),
      });
      if (handled) return;
    }

    // Default: render each block individually (fallback)
    for (const nodeElement of nodeElements) {
      await this._drawTopLevelBlock(
        nodeElement,
        bound,
        padding,
        ctx,
        html2canvas,
        html2canvasOption
      );
    }
  }

  /**
   * Draw a single top-level block to canvas
   */
  private async _drawTopLevelBlock(
    block: GfxBlockElementModel,
    bound: IBound,
    padding: number,
    ctx: CanvasRenderingContext2D,
    html2canvas: (
      element: HTMLElement,
      options?: Record<string, unknown>
    ) => Promise<HTMLCanvasElement>,
    html2canvasOption: Record<string, unknown>
  ) {
    const blockComponent = this.std.view.getBlock(block.id);
    if (!blockComponent) return;

    const blockBound = Bound.deserialize(block.xywh);
    try {
      const canvasData = await html2canvas(
        blockComponent as HTMLElement,
        html2canvasOption
      );
      ctx.drawImage(
        canvasData,
        blockBound.x - bound.x + padding,
        blockBound.y - bound.y + padding,
        blockBound.w,
        blockBound.h
      );
    } catch (error) {
      console.warn('html2canvas failed for block', block.id, error);
    }
  }

  private async _edgelessToCanvas(
    bound: IBound,
    nodes?: GfxBlockElementModel[],
    canvasElements: GfxPrimitiveElementModel[] = [],
    {
      background,
      padding = IMAGE_PADDING,
      dpr = window.devicePixelRatio || 1,
    }: CanvasExportOptions = {}
  ): Promise<HTMLCanvasElement | undefined> {
    const host = this.std.host;
    const rootModel = this.doc.root;
    if (!rootModel) return;

    const html2canvas = (await import('html2canvas')).default;
    if (!(html2canvas instanceof Function)) return;
    if (!this.surface) return;

    const pathname = location.pathname;
    const editorMode = isInsidePageEditor(host);

    // Calculate scale factor to limit canvas size for performance
    const maxDimension = Math.max(bound.w, bound.h);
    const scaleFactor =
      maxDimension > MAX_CANVAS_SIZE ? MAX_CANVAS_SIZE / maxDimension : 1;
    const effectiveDpr = dpr * scaleFactor;

    const canvas = document.createElement('canvas');
    canvas.width = (bound.w + padding * 2) * effectiveDpr;
    canvas.height = (bound.h + padding * 2) * effectiveDpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.scale(effectiveDpr, effectiveDpr);

    const replaceImgSrcWithSvg = this._exportManager?.replaceImgSrcWithSvg;

    const imageProxy = host.std.clipboard.configs.get('imageProxy');
    const html2canvasOption = {
      ignoreElements: function (element: Element) {
        if (
          CANVAS_EXPORT_IGNORE_TAGS.includes(element.tagName) ||
          element.classList.contains('dg')
        ) {
          return true;
        } else {
          return false;
        }
      },

      onclone: async function (documentClone: Document, element: HTMLElement) {
        // html2canvas can't support transform feature
        element.style.setProperty('transform', 'none');
        // Try multiple selectors for the layer
        const layer =
          documentClone.querySelector('gfx-viewport') ||
          documentClone.querySelector('.edgeless-background') ||
          documentClone.querySelector('.affine-edgeless-layer');
        if (layer && layer instanceof HTMLElement) {
          layer.style.setProperty('transform', 'none');
        }

        const boxShadowElements = documentClone.querySelectorAll(
          "[style*='box-shadow']"
        );
        boxShadowElements.forEach(function (element) {
          if (element instanceof HTMLElement) {
            element.style.setProperty('box-shadow', 'none');
          }
        });
        await replaceImgSrcWithSvg?.(element);
      },
      backgroundColor: 'transparent',
      useCORS: imageProxy ? false : true,
      proxy: imageProxy,
    };

    // Collect all node elements including those inside frames
    const nodeElements =
      nodes ??
      (this.gfx.getElementsByBound(bound, {
        type: 'block',
      }) as GfxBlockElementModel[]);

    // Collect frame children for canvas elements
    for (const nodeElement of nodeElements) {
      if (matchModels(nodeElement, [FrameBlockModel])) {
        this.frame.getElementsInFrameBound(nodeElement, false).forEach(ele => {
          if (!isTopLevelBlock(ele)) {
            canvasElements.push(ele as GfxPrimitiveElementModel);
          }
        });
      }
    }

    // PERFORMANCE OPTIMIZATION: Single html2canvas call for all DOM blocks
    // Instead of calling html2canvas N times (once per block), we render
    // the entire edgeless container once and crop the relevant region.
    if (nodeElements.length > 0) {
      // Try multiple selectors - the class name varies between versions
      const edgelessLayer =
        host.querySelector('gfx-viewport') ||
        host.querySelector('.edgeless-background') ||
        host.querySelector('.affine-edgeless-layer');
      if (edgelessLayer && edgelessLayer instanceof HTMLElement) {
        // Get the set of block IDs we want to render
        const selectedBlockIds = new Set<string>();
        for (const node of nodeElements) {
          selectedBlockIds.add(node.id);
          // Also include blocks inside frames
          if (matchModels(node, [FrameBlockModel])) {
            this.frame.getElementsInFrameBound(node, false).forEach(ele => {
              if (isTopLevelBlock(ele)) {
                selectedBlockIds.add(ele.id);
              }
            });
          }
        }

        // Create optimized html2canvas options that filter out non-selected elements
        // and crop to the relevant region
        const optimizedHtml2canvasOption = {
          ...html2canvasOption,
          ignoreElements: (element: Element) => {
            // First check existing ignore rules
            if (
              CANVAS_EXPORT_IGNORE_TAGS.includes(element.tagName) ||
              element.classList.contains('dg')
            ) {
              return true;
            }
            // Ignore blocks that are not in our selection
            // Block components have data-block-id attribute
            const blockId = element.getAttribute('data-block-id');
            if (blockId && !selectedBlockIds.has(blockId)) {
              // Check if this is a top-level block (not nested inside a selected block)
              const parentBlock =
                element.parentElement?.closest('[data-block-id]');
              if (
                !parentBlock ||
                !selectedBlockIds.has(
                  parentBlock.getAttribute('data-block-id') ?? ''
                )
              ) {
                return true;
              }
            }
            return false;
          },
          // Crop the output canvas to only include the relevant region
          // After transform:none is applied in onclone, elements are at their
          // edgeless coordinates. We use x/y to crop and width/height to limit size.
          x: bound.x,
          y: bound.y,
          width: bound.w,
          height: bound.h,
          scale: 1, // We handle scaling ourselves via effectiveDpr
        };

        try {
          // Render only the relevant region of the layer at once
          const layerCanvas = await html2canvas(
            edgelessLayer,
            optimizedHtml2canvasOption
          );

          // The canvas is already cropped to bound dimensions, draw directly
          ctx.drawImage(
            layerCanvas,
            padding, // dest x
            padding // dest y
          );
        } catch {
          // Fallback to per-block rendering if the optimized approach fails
          // (e.g., when Web Components with Shadow DOM are present)
          await this._renderBlocksWithHook(
            nodeElements,
            bound,
            padding,
            ctx,
            html2canvas,
            html2canvasOption
          );
        }
      } else {
        // No edgeless layer found, use per-block rendering
        await this._renderBlocksWithHook(
          nodeElements,
          bound,
          padding,
          ctx,
          html2canvas,
          html2canvasOption
        );
      }

      this._checkCanContinueToCanvas(host, pathname, editorMode);
    }

    // TODO: handle DOM renderer case for clipboard image generation
    if (!(this.surface.renderer instanceof CanvasRenderer)) {
      console.warn(
        'Skipping canvas generation for clipboard: DOM renderer active.'
      );
      return canvas; // Return the empty canvas or handle error
    }

    const surfaceCanvas = this.surface.renderer.getCanvasByBound(
      bound,
      canvasElements
    );
    ctx.drawImage(surfaceCanvas, padding, padding, bound.w, bound.h);

    return canvas;
  }

  private _emitSelectionChangeAfterPaste(
    canvasElementIds: string[],
    blockIds: string[]
  ) {
    const newSelected = [
      ...canvasElementIds,
      ...blockIds.filter(id => {
        return isTopLevelBlock(this.doc.getModelById(id));
      }),
    ];

    this.selectionManager.set({
      editing: false,
      elements: newSelected,
    });
  }

  private async _pasteShapesAndBlocks(
    elementsRawData: (SerializedElement | BlockSnapshot)[],
    pasteCenter: IVec
  ) {
    const [_, { createdElementsPromise }] = this.std.command.exec(
      createElementsFromClipboardDataCommand,
      {
        elementsRawData,
        pasteCenter,
      }
    );
    if (!createdElementsPromise) return;
    const { canvasElements, blockModels } = await createdElementsPromise;
    this._emitSelectionChangeAfterPaste(
      canvasElements.map(ele => ele.id),
      blockModels.map(block => block.id)
    );
  }

  private async _pasteTextContentAsNote(
    content: BlockSnapshot[] | string,
    point: IVec
  ) {
    if (content === '') {
      return;
    }

    const [x, y] = point;

    // Handle string content - always create a note
    if (typeof content === 'string') {
      const noteProps = {
        xywh: new Bound(
          x,
          y,
          DEFAULT_NOTE_WIDTH,
          DEFAULT_NOTE_HEIGHT
        ).serialize(),
      };

      const noteId = this.crud.addBlock(
        'affine:note',
        noteProps,
        this.doc.root!.id
      );

      this.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
        control: 'canvas:paste',
        page: 'whiteboard editor',
        module: 'toolbar',
        segment: 'toolbar',
        type: 'note',
      });

      splitIntoLines(content).forEach((line, idx) => {
        this.crud.addBlock(
          'affine:paragraph',
          { text: new Y.Text(line) },
          noteId,
          idx
        );
      });

      this.gfx.selection.set({
        elements: [noteId],
        editing: false,
      });
      this.gfx.tool.setTool(DefaultTool);
      return;
    }

    // Check if blocks can be pasted directly as GFX blocks (on root/page)
    // Database has parent: ['affine:note', '@root'] - so it can be a direct child of root
    const schema = this.doc.schema;
    const rootBlocks: BlockSnapshot[] = [];
    const noteBlocks: BlockSnapshot[] = [];

    for (const block of content) {
      // Special case: If this is a note containing a database that can be a root child,
      // extract the database directly (this happens when copying an edgeless database)
      if (block.flavour === 'affine:note' && block.children) {
        for (const child of block.children) {
          if (
            child.flavour === 'affine:database' &&
            schema.safeValidate(child.flavour, 'affine:page')
          ) {
            // Database can be a direct root child - extract it from the note wrapper
            rootBlocks.push(child);
          } else {
            // Other children stay in the note
            noteBlocks.push(block);
            break; // Don't process more children, the whole note goes to noteBlocks
          }
        }
        continue;
      }

      // Blocks that can be children of root/page (like database with xywh)
      // Check if the block can be a child of affine:page (@root)
      if (schema.safeValidate(block.flavour, 'affine:page')) {
        rootBlocks.push(block);
      } else {
        noteBlocks.push(block);
      }
    }

    const pastedIds: string[] = [];

    // Paste GFX blocks directly on root (not in a note)
    if (rootBlocks.length > 0) {
      const root = this.doc.root;
      if (root) {
        for (const block of rootBlocks) {
          // Update xywh to paste position
          if (block.props.xywh) {
            const bound = Bound.deserialize(block.props.xywh as string);
            block.props.xywh = new Bound(x, y, bound.w, bound.h).serialize();
          } else {
            block.props.xywh = new Bound(x, y, 400, 200).serialize();
          }
          // Remove index to get fresh one
          delete block.props.index;

          const newId = await this.onBlockSnapshotPaste(
            block,
            this.doc,
            root.id
          );
          if (newId) {
            pastedIds.push(newId);
            this.std
              .getOptional(TelemetryProvider)
              ?.track('CanvasElementAdded', {
                control: 'canvas:paste',
                page: 'whiteboard editor',
                module: 'toolbar',
                segment: 'toolbar',
                type: block.flavour.replace('affine:', ''),
              });
          }
        }
      }
    }

    // Paste remaining blocks in a note
    if (noteBlocks.length > 0) {
      const noteProps = {
        xywh: new Bound(
          x + (rootBlocks.length > 0 ? 450 : 0),
          y,
          DEFAULT_NOTE_WIDTH,
          DEFAULT_NOTE_HEIGHT
        ).serialize(),
      };

      const noteId = this.crud.addBlock(
        'affine:note',
        noteProps,
        this.doc.root!.id
      );
      pastedIds.push(noteId);

      this.std.getOptional(TelemetryProvider)?.track('CanvasElementAdded', {
        control: 'canvas:paste',
        page: 'whiteboard editor',
        module: 'toolbar',
        segment: 'toolbar',
        type: 'note',
      });

      for (const blockSnapshot of noteBlocks) {
        if (blockSnapshot.flavour === 'affine:note') {
          for (const child of blockSnapshot.children) {
            await this.onBlockSnapshotPaste(child, this.doc, noteId);
          }
          continue;
        }
        await this.onBlockSnapshotPaste(blockSnapshot, this.doc, noteId);
      }
    }

    if (pastedIds.length > 0) {
      this.gfx.selection.set({
        elements: pastedIds,
        editing: false,
      });
    }
    this.gfx.tool.setTool(DefaultTool);
  }

  copy() {
    document.dispatchEvent(
      new Event('copy', {
        bubbles: true,
        cancelable: true,
      })
    );
  }

  override mounted() {
    if (!navigator.clipboard) {
      console.error(
        'navigator.clipboard is not supported in current environment.'
      );
      return;
    }
    if (this._disposables.disposed) {
      this._disposables = new DisposableGroup();
    }
    this._init();
    this._initEdgelessClipboard();
  }

  async toCanvas(
    blocks: GfxBlockElementModel[],
    shapes: ShapeElementModel[],
    options?: CanvasExportOptions
  ) {
    blocks.sort(compareLayer);
    shapes.sort(compareLayer);

    const bounds: IBound[] = [];
    blocks.forEach(block => {
      bounds.push(Bound.deserialize(block.xywh));
    });
    shapes.forEach(shape => {
      bounds.push(shape.elementBound);
    });
    const bound = getCommonBound(bounds);
    if (!bound) {
      console.error('bound not exist');
      return;
    }

    const canvas = await this._edgelessToCanvas(bound, blocks, shapes, options);
    return canvas;
  }
}
